import { call, put, all, takeEvery, race, take, takeLatest, takeLeading } from 'typed-redux-saga/macro'
import { SagaReturnType } from 'redux-saga/effects'
import * as Sentry from '@sentry/react'

import {
  GET_ADS,
  CLEAR_ADS,
  getAdsSuccess,
  getAdsFailure,
  CREATE_AD,
  createAdSuccess,
  createAdFailure,
  CREATE_ADS_BATCH,
  createAdsBatchSuccess,
  createAdsBatchFailure,
  DELETE_AD,
  deleteAdSuccess,
  deleteAdFailure,
  GET_AD,
  getAdSuccess,
  getAdFailure,
  UPDATE_AD,
  updateAdSuccess,
  updateAdFailure,
  UPDATE_AD_STATUS,
  updateAdStatusSuccess,
  updateAdStatusFailure,
  VALIDATE_AD,
  validateAdSuccess,
  validateAdFailure,
  APPROVE_INTERNAL_AD,
  approveInternalAdSuccess,
  approveInternalAdFailure,
  DISAPPROVE_INTERNAL_AD,
  disapproveInternalAdSuccess,
  disapproveInternalAdFailure,
  COPY_AD,
  copyAdSuccess,
  getAd,
  copyAdFailure,
  CLEAR_FACEBOOK_POSTS,
  getFacebookPostsSuccess,
  getFacebookPostsFailure,
  GET_FACEBOOK_POSTS,
  deleteCombinedAd
} from '../actions/ads'
import {
  getAdsService,
  createAdService,
  createAdsBatchService,
  updateAdService,
  updateAdStatusService,
  deleteAdService,
  getAdService,
  copyAdService,
  deleteInternalAdService,
  approveInternalAdService,
  disapproveInternalAdService,
  getFacebookPostsService,
  validateAdService
} from '../services/ads'
import { showToasts } from '../../helpers/toasts'
import { TOAST_TYPE } from '../../constants/other'
import { FACEBOOK_PLATFORM } from '../../constants/selectLists/platformList'
import {
  ApproveInternalAdPayload,
  CopyAdPayload,
  CreateAdPayload,
  CreateAdsBatchPayload,
  DeleteAdPayload,
  DisapproveInternalAdPayload,
  GetAdPayload,
  GetAdsPayload,
  GetFacebookPostsPayload,
  UpdateAdPayload,
  UpdateAdStatusPayload,
  ValidateAdPayload
} from '../types/ads'

function* adsWatcher() {
  yield* all([
    // PLOP_APPEND_PATTERN_ANCHOR_WATCHER
    takeEvery(GET_ADS, getAds),
    takeEvery(GET_AD, getAdWorker),
    takeLeading(CREATE_AD, createAd),
    takeEvery(CREATE_ADS_BATCH, createAdsBatchWorker),
    takeLatest(VALIDATE_AD, validateAd),
    takeEvery(UPDATE_AD, updateAdWorker),
    takeEvery(UPDATE_AD_STATUS, updateAdStatusWorker),
    takeEvery(DELETE_AD, deleteAdWorker),
    takeEvery(APPROVE_INTERNAL_AD, approveInternalAdWorker),
    takeEvery(DISAPPROVE_INTERNAL_AD, disapproveInternalAdWorker),
    takeEvery(COPY_AD, copyAdWorker),
    takeEvery(GET_FACEBOOK_POSTS, getFacebookPostsWorker)
  ])
}

// PLOP_APPEND_PATTERN_ANCHOR_WORKER

function* getAds({ params, platform }: GetAdsPayload): Generator {
  try {
    const { response }: SagaReturnType<typeof getAdsService> = yield race({
      response: call(getAdsService, params, platform),
      // need to cancel previous task, if user change the page, but task still in progress
      cancel: take(CLEAR_ADS)
    })

    if (response) {
      yield put(getAdsSuccess(response))
    }
  } catch (error: any) {
    yield put(getAdsFailure(error))
  }
}

function* getAdWorker({ params, platform, pushToList }: GetAdPayload): Generator {
  try {
    const adData: SagaReturnType<typeof getAdService> = yield call(getAdService, params, platform)

    yield put(getAdSuccess(adData, pushToList))
  } catch (error: any) {
    yield put(getAdFailure(error))
  }
}

function* createAd({ adData, platform }: CreateAdPayload): Generator {
  try {
    const response = yield call(createAdService, adData, platform)

    yield put(createAdSuccess(response))

    // additional data and parameters for debugging ad creation
    Sentry.setExtra('createAd', {
      adData,
      response
    })
  } catch (error: any) {
    yield put(createAdFailure(error))
  }
}

function* createAdsBatchWorker({ adsData, platform }: CreateAdsBatchPayload): Generator {
  try {
    type Response = SagaReturnType<typeof createAdsBatchService>
    const response: Response = yield call(createAdsBatchService, adsData, platform)

    yield put(createAdsBatchSuccess(response))
  } catch (error: any) {
    yield put(createAdsBatchFailure(error))
  }
}

function* validateAd({ adData, platform }: ValidateAdPayload): Generator {
  const conversionDomain = adData.conversion_domain
  try {
    const response = yield call(validateAdService, adData, platform)
    yield put(validateAdSuccess(response, conversionDomain))
    Sentry.setExtra('validateAd', {
      adData,
      response
    })
  } catch (error: any) {
    yield put(validateAdFailure(error, conversionDomain))
  }
}

function* updateAdWorker({ adData, id, platform, updateOptions }: UpdateAdPayload): Generator {
  try {
    const { requestMethod } = updateOptions

    const response: SagaReturnType<typeof updateAdService> = yield call(
      updateAdService,
      adData,
      id,
      platform,
      requestMethod
    )

    const thumbnail = response.creative?.image_url || response.creative?.object_story_spec?.video_data?.image_url

    const mockedResponse = {
      ...response,
      ...(platform === FACEBOOK_PLATFORM && { thumbnail })
    }

    yield put(updateAdSuccess(mockedResponse))
  } catch (e: any) {
    yield put(updateAdFailure(e))
  }
}

function* updateAdStatusWorker({ updateData }: UpdateAdStatusPayload): Generator {
  try {
    type Response = SagaReturnType<typeof updateAdStatusService>
    const response: Response = yield call(updateAdStatusService, updateData)

    yield put(updateAdStatusSuccess({ ...response, status: updateData.status }))
  } catch (e: any) {
    yield put(updateAdStatusFailure(e))
  }
}

function* deleteAdWorker({ params, id, platform, deleteOptions }: DeleteAdPayload): Generator {
  try {
    const { deleteType } = deleteOptions

    if (deleteType === 'deleteInternalAd') {
      yield call(deleteInternalAdService, id, platform)
    } else {
      yield call(deleteAdService, id, params, platform)
    }

    if (deleteOptions?.deleteCombinedAd) {
      yield put(deleteCombinedAd(id))
    }

    yield put(deleteAdSuccess(id))
  } catch (e: any) {
    yield put(deleteAdFailure(e))
  }
}

function* approveInternalAdWorker({ id }: ApproveInternalAdPayload): Generator {
  try {
    const response: SagaReturnType<typeof approveInternalAdService> = yield call(approveInternalAdService, id)

    const modifiedResponse = {
      ...response,
      // set approval_status to null, because BE doesn't modify this field after approval
      approval_status: null
    }

    yield put(approveInternalAdSuccess(modifiedResponse, id))

    showToasts({
      type: TOAST_TYPE.success,
      message: 'Ad approved successfully'
    })
  } catch (error: any) {
    yield put(approveInternalAdFailure(error, id))
  }
}

function* disapproveInternalAdWorker({ id, data }: DisapproveInternalAdPayload): Generator {
  try {
    yield call(disapproveInternalAdService, id, data)

    yield put(disapproveInternalAdSuccess(id))
  } catch (error: any) {
    yield put(disapproveInternalAdFailure(error, id))
  }
}

function* copyAdWorker({ data, adId, platform }: CopyAdPayload): Generator {
  try {
    const response: SagaReturnType<typeof copyAdService> = yield call(copyAdService, data, adId, platform)

    yield put(
      copyAdSuccess({
        ...response,
        adset_id: data.adset_id
      })
    )
    // get duplicated ad data and push it to ads list
    const responseCopiedAdId = response.copied_ad_id
    yield put(getAd({ account: data.account, id: responseCopiedAdId, internal: !!data.internal }, platform, true))
  } catch (e: any) {
    yield put(copyAdFailure(e))
  }
}

function* getFacebookPostsWorker({ params }: GetFacebookPostsPayload): Generator {
  try {
    const { response }: SagaReturnType<typeof getFacebookPostsService> = yield race({
      response: call(getFacebookPostsService, params),
      // need to cancel previous task, if user change the page, but task still in progress
      cancel: take(CLEAR_FACEBOOK_POSTS)
    })

    if (response) {
      yield put(getFacebookPostsSuccess(response))
    }
  } catch (error: any) {
    yield put(getFacebookPostsFailure(error))
  }
}

export default adsWatcher
